// First-party visitor + session cookie management. All values are
// opaque UUIDs — no PII, no derived identifiers, no fingerprinting.
//
// `mg_vid`  — persistent visitor id, 1 year sliding expiry.
// `mg_sid`  — session id, 30 min sliding expiry (rolls with each page view).
//
// Cookies are set on the eTLD+1 so dicelacancion.com and
// www.dicelacancion.com share the same visitor, but a separate cookie
// scope is used on mixgenie.vernalabs.net (no shared identity across
// brand domains — intentional).

const VISITOR_KEY = 'mg_vid';
const SESSION_KEY = 'mg_sid';
const VISITOR_MAX_AGE = 365 * 24 * 60 * 60;       // 1 year in seconds
const SESSION_MAX_AGE = 30 * 60;                  // 30 minutes in seconds

function uuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback: 122 bits of randomness using getRandomValues. Sufficient
  // collision resistance for visitor identity on a single-site catalog.
  const bytes = new Uint8Array(16);
  (typeof crypto !== 'undefined' ? crypto : (window as any).msCrypto).getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const target = `${name}=`;
  for (const part of document.cookie.split('; ')) {
    if (part.startsWith(target)) return decodeURIComponent(part.slice(target.length));
  }
  return undefined;
}

// Pick the broadest cookie scope safely. For dicelacancion.com →
// `.dicelacancion.com` (covers www subdomain). For single-host setups
// (localhost, mixgenie.vernalabs.net), leave Domain unset so the cookie
// scopes to exactly the current host.
function cookieDomain(): string | undefined {
  if (typeof location === 'undefined') return undefined;
  const host = location.hostname;
  if (host === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(host)) return undefined;
  const parts = host.split('.');
  if (parts.length >= 2) {
    const lastTwo = parts.slice(-2).join('.');
    // Don't broaden multi-tenant subdomains under vernalabs.net — each
    // product gets its own visitor identity.
    if (lastTwo === 'vernalabs.net') return undefined;
    return `.${lastTwo}`;
  }
  return undefined;
}

function writeCookie(name: string, value: string, maxAgeSeconds: number) {
  if (typeof document === 'undefined') return;
  const parts = [
    `${name}=${encodeURIComponent(value)}`,
    `Max-Age=${maxAgeSeconds}`,
    'Path=/',
    'SameSite=Lax',
  ];
  if (location.protocol === 'https:') parts.push('Secure');
  const domain = cookieDomain();
  if (domain) parts.push(`Domain=${domain}`);
  document.cookie = parts.join('; ');
}

export interface VisitorIdentity {
  visitorId: string;
  sessionId: string;
  /** True if this call created (or rolled) the session cookie. */
  isSessionStart: boolean;
}

// Read-or-create the visitor + session cookies. Always extends both
// expiries so an active visitor isn't logged out of analytics after a
// year of weekly visits.
export function ensureIdentity(): VisitorIdentity {
  let visitorId = readCookie(VISITOR_KEY);
  if (!visitorId) {
    visitorId = uuid();
  }
  writeCookie(VISITOR_KEY, visitorId, VISITOR_MAX_AGE);

  let sessionId = readCookie(SESSION_KEY);
  const isSessionStart = !sessionId;
  if (!sessionId) sessionId = uuid();
  writeCookie(SESSION_KEY, sessionId, SESSION_MAX_AGE);

  return { visitorId, sessionId, isSessionStart };
}
